import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { PageHeader } from '@/shared/PageHeader';
import { useAuth } from '@/shared/AuthContext';

interface MyTenant { id: number; name: string; slug: string; latitude: number; longitude: number; enabled: boolean }
interface UserSummary { id: string; nickname: string; email: string; approved: boolean; isOperative: boolean; roles: string[]; homeTenantId: number }

export function MyTenantPage(): React.JSX.Element {
  usePageTitle('Mio Distaccamento');
  const qc = useQueryClient();
  const toast = useToast();
  const { user, hasRole } = useAuth();
  const [editing, setEditing] = useState(false);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');

  const isAdmin = hasRole('Amministratore', 'SuperAdmin');

  const { data: myTenant } = useQuery<MyTenant>({
    queryKey: ['admin.my-tenant'],
    queryFn: async () => (await api.get('/admin/my-tenant')).data,
    enabled: !!user,
  });

  useEffect(() => {
    if (myTenant) { setLat(String(myTenant.latitude ?? '')); setLng(String(myTenant.longitude ?? '')); }
  }, [myTenant]);

  const { data: allUsers } = useQuery<UserSummary[]>({
    queryKey: ['admin.users'],
    queryFn: async () => (await api.get('/admin/users')).data,
    enabled: !!user,
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      await api.put('/admin/my-tenant', {
        latitude: parseFloat(lat) || 0,
        longitude: parseFloat(lng) || 0,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin.my-tenant'] });
      toast.show('Coordinate aggiornate', 'success');
      setEditing(false);
    },
    onError: () => toast.show('Errore aggiornamento', 'error'),
  });

  function useMyPosition(): void {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      p => { setLat(String(p.coords.latitude)); setLng(String(p.coords.longitude)); },
      () => toast.show('Impossibile ottenere la posizione', 'error'),
    );
  }

  if (!user) return <div className="page-container">Caricamento...</div>;

  const tenantUsers = (allUsers ?? []).filter(u => u.homeTenantId === user.tenantId);
  const approved = tenantUsers.filter(u => u.approved);
  const pending = tenantUsers.filter(u => !u.approved);
  const operatives = approved.filter(u => u.isOperative);

  return (
    <div className="page-container">
      <PageHeader title="🏢 Mio Distaccamento" />

      <div style={{ background: '#fff', borderRadius: 10, padding: 24, marginTop: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <h2 style={{ marginTop: 0, color: '#fd7e14' }}>{myTenant?.name ?? '—'}</h2>
        <p style={{ color: '#666', fontSize: 14 }}>ID Distaccamento: <code>{user?.homeTenantId ?? '—'}</code> · Slug: <code>{myTenant?.slug}</code></p>

        {/* Coordinate GPS (editabili dall'Amministratore) */}
        <div style={{ background: '#f8f9fa', borderLeft: '4px solid #007bff', padding: 16, borderRadius: 6, marginTop: 16 }}>
          <h3 style={{ marginTop: 0, fontSize: 16 }}>📍 Coordinate distaccamento</h3>
          <p style={{ fontSize: 13, color: '#666', margin: '0 0 12px' }}>
            Usate per il calcolo ETA e la visualizzazione mappa nelle selettive attive.
          </p>
          {!editing ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <span><strong>Latitudine:</strong> {myTenant?.latitude || <em style={{ color: '#dc3545' }}>non impostata</em>}</span>
              <span><strong>Longitudine:</strong> {myTenant?.longitude || <em style={{ color: '#dc3545' }}>non impostata</em>}</span>
              {isAdmin && (
                <button onClick={() => setEditing(true)}
                  style={{ padding: '6px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
                           background: '#007bff', color: 'white', fontWeight: 600 }}>
                  ✏️ Modifica
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <label>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Latitudine</div>
                <input type="number" step="0.000001" value={lat} onChange={e => setLat(e.target.value)}
                  placeholder="44.327" style={{ width: '100%', padding: 8, border: '1px solid #ced4da', borderRadius: 6 }} />
              </label>
              <label>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Longitudine</div>
                <input type="number" step="0.000001" value={lng} onChange={e => setLng(e.target.value)}
                  placeholder="10.594" style={{ width: '100%', padding: 8, border: '1px solid #ced4da', borderRadius: 6 }} />
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
                <button onClick={useMyPosition}
                  style={{ padding: '8px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
                           background: '#17a2b8', color: 'white', fontWeight: 600 }}>
                  📍 Usa posizione attuale
                </button>
                <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
                  style={{ padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer',
                           background: '#28a745', color: 'white', fontWeight: 700 }}>💾 Salva</button>
                <button onClick={() => { setEditing(false); if (myTenant) { setLat(String(myTenant.latitude)); setLng(String(myTenant.longitude)); } }}
                  style={{ padding: '8px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
                           background: '#6c757d', color: 'white' }}>Annulla</button>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginTop: 20 }}>
          <StatCard label="Utenti totali" value={tenantUsers.length} color="#007bff" />
          <StatCard label="Approvati" value={approved.length} color="#28a745" />
          <StatCard label="Operativi" value={operatives.length} color="#fd7e14" />
          <StatCard label="In attesa" value={pending.length} color="#dc3545" />
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: 20, marginTop: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <h3 style={{ marginTop: 0 }}>Personale del distaccamento</h3>
        {tenantUsers.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', padding: 30 }}>Nessun utente registrato</div>
        ) : (
          // Bryan: wrap scrollabile lateralmente per non rovinare l'impaginazione
          // su schermi piccoli (la tabella ha molte colonne).
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 600 }}>
            <thead style={{ background: '#343a40', color: 'white' }}>
              <tr>
                <th style={{ padding: 10, textAlign: 'left' }}>Nickname</th>
                <th style={{ padding: 10, textAlign: 'left' }}>Email</th>
                <th style={{ padding: 10 }}>Ruoli</th>
                <th style={{ padding: 10 }}>Stato</th>
              </tr>
            </thead>
            <tbody>
              {tenantUsers.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 10, fontWeight: 600 }}>{u.nickname}</td>
                  <td style={{ padding: 10, color: '#666' }}>{u.email}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>
                    {u.roles.map(r => (
                      <span key={r} style={{ display: 'inline-block', margin: 2, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                                              background: '#e7f3ff', color: '#0056b3' }}>{r}</span>
                    ))}
                  </td>
                  <td style={{ padding: 10, textAlign: 'center' }}>
                    {!u.approved ? (
                      <span style={{ padding: '4px 10px', borderRadius: 4, background: '#fff3cd', color: '#856404', fontSize: 12 }}>In attesa</span>
                    ) : u.isOperative ? (
                      <span style={{ padding: '4px 10px', borderRadius: 4, background: '#d4edda', color: '#155724', fontSize: 12 }}>Operativo</span>
                    ) : (
                      <span style={{ padding: '4px 10px', borderRadius: 4, background: '#e2e3e5', color: '#383d41', fontSize: 12 }}>Non operativo</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        <p style={{ color: '#888', fontSize: 12, marginTop: 16 }}>
          Per modificare ruoli e stato vai a <a href="/admin">Gestione Utenti</a>.
        </p>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }): React.JSX.Element {
  return (
    <div style={{ background: '#f8f9fa', borderLeft: `4px solid ${color}`, padding: 16, borderRadius: 6 }}>
      <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
